import * as ol from 'ol';
import { Feature } from 'ol';
import { Coordinate, distance } from 'ol/coordinate';
import { FeatureLike } from 'ol/Feature';
import { SimpleGeometry } from 'ol/geom';
import MultiLineString from 'ol/geom/MultiLineString';
import Polygon from 'ol/geom/Polygon';
import Draw, { SketchCoordType } from 'ol/interaction/Draw';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';

import { warn, log } from '@/utils/logger';

import { px2mm, realLengthWithUnit, PAGE_UNIT, TKEvent } from '../types';

const finalMeasureResultColor = '#000000';

export const rulerSpanId = 'ruler-value';

// ruler line height
let smallerRatio = 1;
let rulerLineHeight = 20;
let tickWdith = 10;
let tickHeight = 10;

/**
 * adjust ruler ratio at each start of draw
 * @log first adjustment ratio - @2025/04/30,05/01
 * @param zoomLevel
 */
const adjustRulerRatio = (zoomLevel: number) => {
  const limitedZoomlevel = Math.min(4.8, zoomLevel);
  smallerRatio = (20 - 3.4 * limitedZoomlevel) / limitedZoomlevel;
  rulerLineHeight = 20 * smallerRatio;
  tickWdith = 10 * smallerRatio;
  tickHeight = 10 * smallerRatio;
};

/**
 * Figure out text scale
 * @param zoomLevel map zoom level
 * @returns new scale
 */
const resizeTextBy = (zoomLevel: number) => {
  return Math.pow(zoomLevel / 3, 2) * smallerRatio * 0.5;
};

// const featureDefaultStyle = new Style({
//   fill: new Fill({
//     color: 'rgba(255,255,255,0.4)',
//   }),
//   stroke: new Stroke({
//     color: '#3399CC',
//     width: 1.25,
//   }),
// });

// style for ticks
const tickStyle = new Style({
  stroke: new Stroke({
    color: '#3F99F7', // was #3F99F7
    width: 1,
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: '#3399CC',
      width: 1.5,
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.4)',
    }),
  }),
});

// style for tip text
const tipStyle = new Style({
  text: new Text({
    font: 'italic 12px Calibri,sans-serif',
    fill: new Fill({
      // initial text color for `Click to meaure`
      color: 'rgba(255, 255, 255, 1)',
    }),
    // for point feature of draw
    backgroundFill: new Fill({
      color: 'rgba(0, 0, 0, 0.4)',
    }),
    padding: [2, 2, 2, 2],
    offsetX: -16, // ! for box
    textAlign: 'right', // ! for box
    offsetY: -12,
    textBaseline: 'top',
    // TODO: make this dynamic based on zoom...
    scale: 1.2,
  }),
});

// style for ruler sketch
const sketchDefaultStyle = new Style({
  fill: new Fill({
    color: 'rgba(255, 255, 255, 0.2)',
  }),
  stroke: new Stroke({
    color: '#3F99F7',
    width: 2,
    lineDash: [4, 8], // fancy dash pattern while doing zoom in/out
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: '#3F99F7',
      width: 1.5,
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.4)',
    }),
  }),
});

/**
 * Format length in real size of map.
 * @param coordinats coordinates of line
 */
export const formatRealLength = function (
  coordinates: Coordinate[],
  map: ol.Map,
) {
  const lengthInPix = distance(coordinates[0], coordinates[1]);
  const pageObject = map.get('page');
  if (!pageObject) {
    warn(`## no page object to figure out length!`);
    return 'unknown';
  }
  const { scale, unit } = pageObject;
  const mmLenth = px2mm(lengthInPix);
  const isMetric = unit === PAGE_UNIT.M;
  const output = realLengthWithUnit(mmLenth, isMetric, scale);
  return ` ${output}`;
};

/**
 * Create ruler box
 * @param sketch actually a pair of coordinates
 * @param geometry
 * @returns
 */
const rotatableRulerBox = (
  sketch: SketchCoordType,
  geometry: SimpleGeometry,
) => {
  const coordinates = sketch as Coordinate[];
  // start point of ruler line
  const first = coordinates[0];
  // end point of ruler line
  const last = coordinates[coordinates.length - 1];
  const dx = first[0] - last[0];
  const dy = first[1] - last[1];
  // rotation of ruler line
  const rotation = Math.atan2(dy, dx);
  // new coordinates of the ruler box
  const newCoordinates = [first];

  // figure out the end point of height line which is perpendicular to the ruler line
  const endPoint = [
    first[0] + rulerLineHeight * Math.cos(rotation - Math.PI / 2),
    first[1] + rulerLineHeight * Math.sin(rotation - Math.PI / 2),
  ];
  // add the end point to the coordinates
  newCoordinates.push(endPoint);
  // figure out the end point of second height line which is perpendicular to the ruler line
  const endPoint2 = [
    last[0] + rulerLineHeight * Math.cos(rotation - Math.PI / 2),
    last[1] + rulerLineHeight * Math.sin(rotation - Math.PI / 2),
  ];
  // add the end point to the coordinates
  newCoordinates.push(endPoint2);
  // add the last point to the coordinates
  newCoordinates.push(last);
  // add the first point to the coordinates to close the box
  newCoordinates.push(newCoordinates[0].slice());

  if (!geometry) {
    // console.log('New polygon created!');
    const topPoint = [first[0], first[1] + rulerLineHeight];
    // three points to create a triangle
    geometry = new Polygon([[first, topPoint, first]]);
  } else {
    // console.log('Polygon updated size!');
    geometry.setCoordinates([newCoordinates]);
  }

  return geometry;
};

type GemoetryCoords = {
  [key: string]: Coordinate[];
};

export const rulerStyleFunction =
  (baseStyle: Style, targetType: string, map: ol.Map) =>
  (feature: FeatureLike) => {
    const styles = [baseStyle];
    const geometry = feature.getGeometry() as SimpleGeometry;
    const type = geometry.getType();
    let label = 'Click to meaure';

    // rendering ruler skectch and finished ruler polygon
    if (type === targetType) {
      const coordinatesWithType: GemoetryCoords = {
        LineString: geometry.getCoordinates() as Coordinate[],
        MultiLineString: geometry.getCoordinates()?.[0],
        Polygon: geometry.getCoordinates()?.[0],
      };
      const coordinates = coordinatesWithType[targetType];
      // get the bottom left and bottom right coordinates
      const bottomLeft = coordinates[0];
      const bottomRight = coordinates[3] || coordinates[1];

      const dx = bottomRight[0] - bottomLeft[0];
      const dy = bottomRight[1] - bottomLeft[1];
      // ! use external tip style:
      const tipeStyleClone = tipStyle.clone();
      tipeStyleClone.getText()?.setPlacement('line');
      // rotation of ruler line
      const rotation = -Math.atan2(dy, dx);
      // set text color:
      const fill = new Fill({ color: finalMeasureResultColor });
      tipeStyleClone.getText()?.setFill(fill);
      // == Real distance of two coordinates ==
      label = formatRealLength([bottomLeft, bottomRight], map);
      tipeStyleClone.getText()?.setText(label);
      // save label to map @2025/04/30
      map.set('rulerLength', label.trim());
      // log(`## to cache ruler length:${label}`);

      tipeStyleClone.getText()?.setTextBaseline('bottom');
      // update text scale based on map zoom level
      const zoom = map.getView().getZoom() as number;
      const scale = resizeTextBy(zoom);
      tipeStyleClone.getText()?.setScale(scale);
      tipeStyleClone.getText()?.setOffsetY(-15 * scale);
      // add length shadow:
      const shadowTipStyle = tipeStyleClone.clone();
      const shadowFill = new Fill({ color: '#CCCCCC' });
      shadowTipStyle.getText()?.setFill(shadowFill);
      shadowTipStyle.getText()?.setOffsetY(-15 * scale + 2);
      styles.push(shadowTipStyle);
      styles.push(tipeStyleClone);
      // == show rotation text:
      const angleTextStyle = tipeStyleClone.clone();
      const angle = Math.round(rotation * (180 / Math.PI));
      // console.log(`### angle: ${angle}`);
      angleTextStyle.getText()?.setText(` ∠: ${Math.abs(angle)}°`);
      angleTextStyle.getText()?.setTextBaseline('top');
      // add angle shadow:
      const angelShadow = angleTextStyle.clone();
      angelShadow.getText()?.setFill(shadowFill);
      angelShadow.getText()?.setOffsetY(-15 * scale + 2);
      styles.push(angelShadow);
      styles.push(angleTextStyle);
    }
    // == idle state: rendering `Click to meaure` label ==
    if (type === 'Point') {
      tipStyle.getText()?.setText(label);
      styles.push(tipStyle);
    }

    return styles;
  };

/**
 * Create ruler ticks.
 * @param {Array} sketch actually a pair of coordinate
 * @param {MultiLineString} geometry
 */
const createRulerTicks = (
  sketch: SketchCoordType,
  geometry: SimpleGeometry,
) => {
  const coordinates = sketch as Coordinate[];
  const startPoint = coordinates[0];
  const endPoint = coordinates[1];

  const diffX = endPoint[0] - startPoint[0];
  const diffY = endPoint[1] - startPoint[1];
  // rotation of ruler line
  const rotation = Math.atan2(diffY, diffX);

  // figure out the distance of two points
  const totalDistance = Math.sqrt(diffX * diffX + diffY * diffY);
  // console.log('Real distance:', totalDistance);

  const numberOfTicks = Math.floor(totalDistance / Math.abs(tickWdith));
  // console.log('number Of segments:', numberOfHorizontalLines);

  // const stepY = diffY / numberOfHorizontalLines;
  // draw a horizontal line in every 10000 meters within the distance
  const segments = [];

  // == add base line: ==
  const baseLine = [startPoint, endPoint];
  segments.push(baseLine);

  // == add start vertical tick: ==
  const startVerticalTick = [
    startPoint[0] - 2 * tickHeight * Math.cos(rotation - Math.PI / 2),
    startPoint[1] - 2 * tickHeight * Math.sin(rotation - Math.PI / 2),
  ];
  segments.push([startPoint, startVerticalTick]);

  // == add end vertical tick: ==
  const endVerticalTick = [
    endPoint[0] - 2 * tickHeight * Math.cos(rotation - Math.PI / 2),
    endPoint[1] - 2 * tickHeight * Math.sin(rotation - Math.PI / 2),
  ];
  segments.push([endPoint, endVerticalTick]);

  // == add remaining tickes between start and end vertical ticks: ==
  for (let i = 0; i < numberOfTicks; i++) {
    const dynamicTickHeight = i % 2 === 1 ? tickHeight * 0.8 : tickHeight * 1.4;
    const tickBottomX = startPoint[0] + tickWdith * i * Math.cos(rotation);
    const tickBottomY = startPoint[1] + tickWdith * i * Math.sin(rotation);
    const startOfEachTick = [tickBottomX, tickBottomY]; // this is correct!

    const tickTopX =
      tickBottomX - dynamicTickHeight * Math.cos(rotation - Math.PI / 2);
    const tickTopY =
      tickBottomY - dynamicTickHeight * Math.sin(rotation - Math.PI / 2);
    const endOfEachTick = [tickTopX, tickTopY];

    segments.push([startOfEachTick, endOfEachTick]);
  }

  if (!geometry) {
    // start geometry to present
    geometry = new MultiLineString([coordinates]);
  } else {
    // update geometry to present
    geometry.setCoordinates(segments);
  }
  return geometry;
};

export function addTicksInteraction(map: ol.Map, vector: VectorLayer<Feature>) {
  // ! tuning the style for ticks:
  tipStyle.getText()?.setOffsetX(10);
  tipStyle.getText()?.setTextAlign('left');

  const featureStyle = rulerStyleFunction(tickStyle, 'MultiLineString', map);
  // !! == this will enable `length` & `angle` text visible after drawend ==
  vector.setStyle(featureStyle);

  const source = vector.getSource() as VectorSource;
  const tickDraw = new Draw({
    source,
    type: 'LineString', // ! only can use LineString for now!
    maxPoints: 2,
    geometryFunction: createRulerTicks,
    style: featureStyle, // realtime update length and angle value on sketch mode
  });
  map.addInteraction(tickDraw);
  // cache for later removal
  map.set('tickDraw', tickDraw);
  // clear last one
  tickDraw.on(TKEvent.DRAWSTART, () => {
    source.clear();
    // ! reset ruler size at each start of draw!
    // @2025/04/30
    const zoomLevel = map.getView().getZoom() || 2;
    adjustRulerRatio(zoomLevel);
    // remove last one
    const rulerElement = map
      .getTargetElement()
      .querySelector(`#${rulerSpanId}`);
    rulerElement && rulerElement.remove();
  });
  tickDraw.on(TKEvent.DRAWEND, (event) => {
    // ! reset rotation of tip text
    // tipStyle.getText()?.setRotation(0);
    // ! hack here - to write the ruler value to the map container for testing
    // @2025/04/30
    const cachedRuler = map.get('rulerLength');
    // log(`## to write ruler size from cache: ${cachedRuler}`);
    const label = cachedRuler;
    const span = document.createElement('span');
    span.id = rulerSpanId;
    span.style.display = 'hidden';
    span.textContent = label;
    map.getTargetElement().appendChild(span);
  });

  // disable it at first initialization
  tickDraw.setActive(false);

  return tickDraw;
}

/**
 * ========= remove this at some time ============
 * @deprecated no need to have box to enclose the ruler
 * @param map
 * @param vector
 */
function addRulerInteraction(map: ol.Map, vector: VectorLayer<Feature>) {
  const source = vector.getSource() as VectorSource;
  // add a new draw interaction
  const boxDraw = new Draw({
    source,
    type: 'Circle', // right type to add a ruler
    geometryFunction: rotatableRulerBox,
    style: rulerStyleFunction(sketchDefaultStyle, 'LineString', map),
  });
  map.addInteraction(boxDraw);
  // cache for later removal
  map.set('boxDraw', boxDraw);
  // clear last one
  boxDraw.on(TKEvent.DRAWSTART, () => {
    source.clear();
  });

  boxDraw.on(TKEvent.DRAWEND, (event) => {
    // ! reset rotation of tip text
    // tipStyle.getText()?.setRotation(0);
  });

  // disable it at first initialization
  boxDraw.setActive(false);
}

export function activateRuler(map: ol.Map) {
  const boxDraw = map.get('boxDraw');
  boxDraw && boxDraw.setActive(true);
  const tickDraw = map.get('tickDraw');
  tickDraw && tickDraw.setActive(true);
}

export function deActivateRuler(map: ol.Map) {
  const boxDraw = map.get('boxDraw');
  boxDraw && boxDraw.setActive(false);
  const tickDraw = map.get('tickDraw');
  tickDraw && tickDraw.setActive(false);
}
